import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  /** Исходный файл, который нужно обрезать. Модалка скрыта, если file === null */
  file: File | null;
  /** Соотношение сторон итогового кадра (ширина / высота). По умолчанию квадрат */
  aspect?: number;
  /** Заголовок модалки */
  title?: string;
  /** Рамка выделения в виде круга (для аватаров) */
  round?: boolean;
  /** Максимальная ширина итогового изображения в пикселях */
  maxOutputWidth?: number;
  onCancel: () => void;
  onCropped: (file: File) => void;
}

type Corner = 'nw' | 'ne' | 'sw' | 'se';
interface Box { x: number; y: number; w: number; h: number; }
interface DragState { mode: 'move' | Corner; startX: number; startY: number; startBox: Box; }

const STAGE_MAX = 420;
const MIN_BOX = 32;
const HANDLE_SIZE = 18;

export default function ImageCropModal({
  file,
  aspect = 1,
  title = 'Обрезать изображение',
  round = false,
  maxOutputWidth = 1600,
  onCancel,
  onCropped,
}: Props) {
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [rotation, setRotation] = useState(0); // 0 | 90 | 180 | 270, по часовой
  const [box, setBox] = useState<Box | null>(null);
  const [processing, setProcessing] = useState(false);

  const dragRef = useRef<DragState | null>(null);

  const stageMax = Math.min(STAGE_MAX, typeof window !== 'undefined' ? window.innerWidth - 72 : STAGE_MAX);

  // Загружаем новый файл — полностью сбрасываем состояние
  useEffect(() => {
    if (!file) {
      setImgUrl(null);
      setNatural(null);
      setRotation(0);
      setBox(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    setNatural(null);
    setRotation(0);
    setBox(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleImgLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
  };

  // Габариты изображения на экране с учётом поворота (contain, без апскейла)
  const layout = useMemo(() => {
    if (!natural) return null;
    const effW = rotation % 180 === 0 ? natural.w : natural.h;
    const effH = rotation % 180 === 0 ? natural.h : natural.w;
    const scale = Math.min(stageMax / effW, stageMax / effH, 1);
    return { effW, effH, scale, dispW: effW * scale, dispH: effH * scale };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [natural, rotation, stageMax]);

  const defaultBox = useCallback((dispW: number, dispH: number): Box => {
    let w = dispW;
    let h = w / aspect;
    if (h > dispH) {
      h = dispH;
      w = h * aspect;
    }
    return { x: (dispW - w) / 2, y: (dispH - h) / 2, w, h };
  }, [aspect]);

  // При загрузке фото или повороте — ставим рамку по центру максимального размера
  useEffect(() => {
    if (!layout) return;
    setBox(defaultBox(layout.dispW, layout.dispH));
  }, [layout, defaultBox]);

  const fitFromAnchor = useCallback(
    (anchorX: number, anchorY: number, wanted: number, growX: 1 | -1, growY: 1 | -1): Box => {
      if (!layout) return { x: 0, y: 0, w: MIN_BOX, h: MIN_BOX / aspect };
      let w = Math.max(MIN_BOX, wanted);
      let h = w / aspect;
      const maxW = growX === 1 ? layout.dispW - anchorX : anchorX;
      const maxH = growY === 1 ? layout.dispH - anchorY : anchorY;
      const scale = Math.min(1, maxW / w, maxH / h);
      w = Math.max(MIN_BOX, w * scale);
      h = w / aspect;
      const x = growX === 1 ? anchorX : anchorX - w;
      const y = growY === 1 ? anchorY : anchorY - h;
      return { x, y, w, h };
    },
    [layout, aspect]
  );

  const onBoxPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!box) return;
    dragRef.current = { mode: 'move', startX: e.clientX, startY: e.clientY, startBox: box };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    e.stopPropagation();
  };

  const onHandlePointerDown = (corner: Corner) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (!box) return;
    dragRef.current = { mode: corner, startX: e.clientX, startY: e.clientY, startBox: box };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    e.stopPropagation();
  };

  const onDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || !layout) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    const s = d.startBox;

    if (d.mode === 'move') {
      const x = Math.min(Math.max(0, s.x + dx), layout.dispW - s.w);
      const y = Math.min(Math.max(0, s.y + dy), layout.dispH - s.h);
      setBox({ ...s, x, y });
      return;
    }

    const anchors: Record<Corner, { ax: number; ay: number; gx: 1 | -1; gy: 1 | -1; dw: number }> = {
      se: { ax: s.x, ay: s.y, gx: 1, gy: 1, dw: s.w + dx },
      sw: { ax: s.x + s.w, ay: s.y, gx: -1, gy: 1, dw: s.w - dx },
      ne: { ax: s.x, ay: s.y + s.h, gx: 1, gy: -1, dw: s.w + dx },
      nw: { ax: s.x + s.w, ay: s.y + s.h, gx: -1, gy: -1, dw: s.w - dx },
    };
    const a = anchors[d.mode];
    setBox(fitFromAnchor(a.ax, a.ay, a.dw, a.gx, a.gy));
  };

  const onDragEnd = () => {
    dragRef.current = null;
  };

  const rotateBy = (delta: number) => {
    setRotation(r => (r + delta + 360) % 360);
  };

  const handleConfirm = async () => {
    if (!file || !imgUrl || !natural || !layout || !box) return;
    setProcessing(true);
    try {
      const src = new Image();
      src.src = imgUrl;
      if (!src.complete) {
        await new Promise(resolve => { src.onload = resolve; });
      }

      // 1. Рисуем фото в естественном разрешении с учётом поворота
      const rotCanvas = document.createElement('canvas');
      rotCanvas.width = layout.effW;
      rotCanvas.height = layout.effH;
      const rotCtx = rotCanvas.getContext('2d');
      if (!rotCtx) return;
      rotCtx.translate(layout.effW / 2, layout.effH / 2);
      rotCtx.rotate((rotation * Math.PI) / 180);
      rotCtx.drawImage(src, -natural.w / 2, -natural.h / 2, natural.w, natural.h);

      // 2. Переводим рамку выделения в координаты этого канваса
      const sx = box.x / layout.scale;
      const sy = box.y / layout.scale;
      const sW = box.w / layout.scale;
      const sH = box.h / layout.scale;

      const outW = Math.min(maxOutputWidth, Math.round(sW));
      const outH = Math.round(outW / aspect);

      const outCanvas = document.createElement('canvas');
      outCanvas.width = outW;
      outCanvas.height = outH;
      const ctx = outCanvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(rotCanvas, sx, sy, sW, sH, 0, 0, outW, outH);

      const mime = file.type && file.type.startsWith('image/') ? file.type : 'image/jpeg';
      const blob: Blob | null = await new Promise(resolve => outCanvas.toBlob(resolve, mime, 0.92));
      if (!blob) return;
      const croppedFile = new File([blob], file.name, { type: mime });
      onCropped(croppedFile);
    } finally {
      setProcessing(false);
    }
  };

  if (!file) return null;

  const dispW = layout?.dispW ?? stageMax;
  const dispH = layout?.dispH ?? stageMax / aspect;
  const cursors: Record<Corner, string> = { nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize' };
  const handlePos: Record<Corner, React.CSSProperties> = {
    nw: { left: -HANDLE_SIZE / 2, top: -HANDLE_SIZE / 2 },
    ne: { right: -HANDLE_SIZE / 2, top: -HANDLE_SIZE / 2 },
    sw: { left: -HANDLE_SIZE / 2, bottom: -HANDLE_SIZE / 2 },
    se: { right: -HANDLE_SIZE / 2, bottom: -HANDLE_SIZE / 2 },
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h3 style={styles.title}>{title}</h3>

        <div style={{ ...styles.stage, width: dispW, height: dispH }}>
          {imgUrl && natural && layout && (
            <img
              src={imgUrl}
              alt=""
              draggable={false}
              onLoad={handleImgLoad}
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: natural.w * layout.scale,
                height: natural.h * layout.scale,
                transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
          )}
          {!natural && imgUrl && (
            <img src={imgUrl} alt="" onLoad={handleImgLoad} style={{ display: 'none' }} />
          )}

          {box && layout && (
            <div
              onPointerDown={onBoxPointerDown}
              onPointerMove={onDragMove}
              onPointerUp={onDragEnd}
              onPointerLeave={onDragEnd}
              style={{
                ...styles.cropBox,
                left: box.x,
                top: box.y,
                width: box.w,
                height: box.h,
                borderRadius: round ? '50%' : 4,
              }}
            >
              {(['nw', 'ne', 'sw', 'se'] as Corner[]).map(c => (
                <div
                  key={c}
                  onPointerDown={onHandlePointerDown(c)}
                  onPointerMove={onDragMove}
                  onPointerUp={onDragEnd}
                  onPointerLeave={onDragEnd}
                  style={{
                    ...styles.handle,
                    ...handlePos[c],
                    cursor: cursors[c],
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div style={styles.rotateRow}>
          <button type="button" onClick={() => rotateBy(-90)} style={styles.btnRotate} disabled={processing} title="Повернуть влево на 90°">
            ⟲ Влево
          </button>
          <button type="button" onClick={() => rotateBy(90)} style={styles.btnRotate} disabled={processing} title="Повернуть вправо на 90°">
            ⟳ Вправо
          </button>
        </div>

        <p style={styles.hint}>Перетащите рамку или потяните за уголки, чтобы выбрать область обрезки</p>

        <div style={styles.actions}>
          <button type="button" onClick={onCancel} style={styles.btnCancel} disabled={processing}>
            Отмена
          </button>
          <button type="button" onClick={handleConfirm} style={styles.btnPrimary} disabled={processing || !natural}>
            {processing ? 'Обработка...' : '✂️ Обрезать и выбрать'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  modal: {
    background: '#fff', borderRadius: 12, padding: 'clamp(18px, 4vw, 26px)',
    width: '100%', maxWidth: 460, boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  },
  title: { color: '#1a3a6b', fontSize: 'clamp(1rem, 2vw, 1.1rem)', alignSelf: 'flex-start', margin: 0 },
  stage: {
    position: 'relative', overflow: 'hidden', background: '#111',
    border: '1px solid #dde3ef', touchAction: 'none', flexShrink: 0,
  },
  cropBox: {
    position: 'absolute', touchAction: 'none', cursor: 'move',
    border: '2px solid #fff', boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
  },
  handle: {
    position: 'absolute', width: HANDLE_SIZE, height: HANDLE_SIZE, touchAction: 'none',
    background: '#fff', border: '2px solid #1a3a6b', borderRadius: '50%',
    boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
  },
  rotateRow: { display: 'flex', gap: 10, width: '100%', justifyContent: 'center' },
  btnRotate: {
    padding: '7px 14px', background: '#f0f4fb', color: '#1a3a6b', border: '1px solid #dde3ef',
    borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
  },
  hint: { fontSize: '0.8rem', color: '#888', textAlign: 'center', margin: 0 },
  actions: { display: 'flex', gap: 10, width: '100%', justifyContent: 'flex-end' },
  btnCancel: { padding: '9px 18px', background: '#f0f4fb', color: '#555', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  btnPrimary: { padding: '9px 18px', background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
};
